import { useEffect, useRef, useState } from "react";
import { keyframes, styled, Typography } from "../mui";

//Default timing constants
export const DEFAULT_TYPING_SPEED = 75;
export const DEFAULT_DELETING_SPEED = 35;
export const DEFAULT_PAUSE_AFTER_TYPE = 2500;
export const DEFAULT_PAUSE_AFTER_DELETE = 250;

interface UseTypewriterProps {
  enabled: boolean;
  words: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseAfterType?: number;
  pauseAfterDelete?: number;
}

export const useTypeWriter = ({
  enabled,
  words,
  typingSpeed = DEFAULT_TYPING_SPEED,
  deletingSpeed = DEFAULT_DELETING_SPEED,
  pauseAfterType = DEFAULT_PAUSE_AFTER_TYPE,
  pauseAfterDelete = DEFAULT_PAUSE_AFTER_DELETE,
}: UseTypewriterProps) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const schedule = (callback: () => void, delay: number) => {
    timeoutRef.current = setTimeout(callback, delay);
  };

  useEffect(() => {
    if (!enabled || words.length === 0) {
      setDisplayText("");
      return;
    }

    const currentWord = words[wordIndex];

    if (!isDeleting && displayText.length < currentWord.length) {
      schedule(() => {
        setDisplayText(currentWord.slice(0, displayText.length + 1));
      }, typingSpeed);
    } else if (!isDeleting && displayText.length === currentWord.length) {
      schedule(() => setIsDeleting(true), pauseAfterType);
    } else if (isDeleting && displayText.length > 0) {
      schedule(() => {
        setDisplayText(currentWord.slice(0, displayText.length - 1));
      }, deletingSpeed);
    } else if (isDeleting && displayText.length === 0) {
      schedule(() => {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % words.length);
      }, pauseAfterDelete);
    }

    return clear;
  }, [
    enabled,
    words,
    wordIndex,
    displayText,
    isDeleting,
    typingSpeed,
    deletingSpeed,
    pauseAfterType,
    pauseAfterDelete,
  ]);

  return displayText;
};
const blink = keyframes`
  0%, 50%, 100% { opacity: 1; }
  25%, 75% { opacity: 0; }
`;

export const Placeholder = styled(Typography)(({ theme }) => ({
  position: "absolute",
  left: "16px",
  right: "48px",
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  textWrap: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  color: theme.palette.textLabel.main,
}));

export const StaticText = styled(Typography)(({ theme }) => ({
  display: "inline",
  fontSize: theme.typography.bodyLarge.fontSize,
}));

export const AnimatedText = styled(Typography)(({ theme }) => ({
  display: "inline",
  fontSize: theme.typography.bodyLarge.fontSize,
}));

export const Cursor = styled(Typography)({
  display: "inline-block",
  animation: `${blink} 1s infinite`,
  fontSize: "20px",
});
